import { cn } from "@/lib/utils";
import { InputHTMLAttributes, TextareaHTMLAttributes, forwardRef } from "react";

const PICKER_INPUT_TYPES = new Set([
  "date",
  "datetime-local",
  "time",
  "month",
  "week",
]);

export const Input = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement>
>(({ className, type, onClick, disabled, readOnly, ...props }, ref) => {
  const isPickerType = type != null && PICKER_INPUT_TYPES.has(type);

  return (
    <input
      ref={ref}
      type={type}
      disabled={disabled}
      readOnly={readOnly}
      className={cn(
        "w-full rounded-xl border border-border bg-card px-4 py-2.5 text-sm text-foreground placeholder:text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30",
        isPickerType && "cursor-pointer",
        className
      )}
      onClick={(e) => {
        onClick?.(e);
        if (e.defaultPrevented || disabled || readOnly || !isPickerType) return;
        try {
          e.currentTarget.showPicker?.();
        } catch {
          /* unsupported / already open */
        }
      }}
      {...props}
    />
  );
});
Input.displayName = "Input";

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, ...props }, ref) => (
  <textarea
    ref={ref}
    className={cn(
      "w-full rounded-xl border border-border bg-card px-4 py-2.5 text-sm text-foreground placeholder:text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y min-h-[120px]",
      className
    )}
    {...props}
  />
));
Textarea.displayName = "Textarea";

export function Label({
  className,
  ...props
}: React.LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label
      className={cn("mb-1.5 block text-sm font-medium text-foreground", className)}
      {...props}
    />
  );
}
